import { z } from 'zod';
/**
 * Social Security number input (ADR-012 §5).
 *
 * Exactly nine digits, dashes optional on input in the usual 3-2-4 places, stored without.
 * Refuses the numbers the SSA never issues: area `000` or `666`, group `00`, serial `0000`.
 *
 * Every message names the field, never the value (ADR-012 §5, "error messages name the
 * field"), and no refinement echoes what was typed.
 */
const SHAPE = /^(?:\d{3}-\d{2}-\d{4}|\d{9})$/;
/** True for an ITIN-range area (9xx). An ITIN does not authorise W-2 employment. */
export function isItinArea(ssn) {
    return ssn.startsWith('9');
}
/**
 * The format alone: nine digits that could have been issued, ITIN areas included. Use it
 * directly only where no person is in view; a person's number goes through `ssnInputSchema`.
 */
export const ssnFormatSchema = z
    .string()
    .trim()
    .regex(SHAPE, 'SSN must be nine digits')
    .transform((value) => value.replaceAll('-', ''))
    .superRefine((ssn, ctx) => {
    const area = ssn.slice(0, 3);
    if (area === '000' || area === '666')
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'SSN area number is not valid' });
    if (ssn.slice(3, 5) === '00')
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'SSN group number is not valid' });
    if (ssn.slice(5) === '0000')
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'SSN serial number is not valid' });
});
/**
 * A person's SSN, judged against their employment classification (ADR-012 Q6). A `9xx` number
 * is an ITIN, accepted only for a `1099` contractor; everyone else — W-2, or not yet classified —
 * is asked for a Social Security number. The classification comes from the person record, never
 * from the payload that carries the number.
 */
export function ssnInputSchema(classification) {
    return ssnFormatSchema.superRefine((ssn, ctx) => {
        if (isItinArea(ssn) && classification !== '1099') {
            ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'An ITIN is accepted only for 1099 contractors; enter a Social Security number' });
        }
    });
}
/** The plain last four digits kept beside the encrypted value, for masked display without a decrypt. */
export function ssnLast4(normalisedSsn) {
    if (!/^\d{9}$/.test(normalisedSsn))
        throw new Error('ssnLast4 takes a normalised nine-digit SSN');
    return normalisedSsn.slice(5);
}
//# sourceMappingURL=ssn.js.map